import {
  IconAngleRight,
  IconBuilding,
  IconUserCircle,
  mapper,
} from '@maia/icons';
import { ActionButtonLink, TableCell } from '@maia/ui';
import { ReactNode } from '@tanstack/react-router';
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { match } from 'ts-pattern';
import { Insurances } from './queryOptions';

type Insurance = Insurances['response'][number];
type Customer = Insurance['customer'];

const columns: ColumnDef<Insurance>[] = [
  {
    accessorKey: 'reference',
    header: 'Polisnummer',
    enableSorting: false,
    cell: ({ row }) => (
      <TableCell.Link
        to="/insurances/$guid"
        params={{
          guid: row.original.guid,
        }}
      >
        {row.original.reference}
      </TableCell.Link>
    ),
  },
  {
    accessorKey: 'title',
    header: 'Productnaam',
    enableSorting: false,
    cell: ({ row }) => (
      <TableCell.Product
        icon={mapper(row.original.icon)}
        title={row.original.title}
        characteristic={row.original.characteristic}
        isSpecialLimit={row.original.isSpecialLimit}
      />
    ),
  },
  {
    accessorKey: 'customer',
    header: 'Klantnaam',
    enableSorting: false,
    cell: ({ row }) => (
      <TableCell.Activity
        icon={mapCustomerToIcon(row.original.customer)}
        title={row.original.customer.consumer.fullName}
      />
    ),
  },
  {
    accessorKey: 'effectiveDate',
    header: 'Startdatum',
    cell: ({ row }) => <TableCell.Date date={row.original.effectiveDate} />,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => (
      <TableCell.Tag
        {...row.original.status}
        tooltip={row.original.acceptanceRemark ?? undefined}
      />
    ),
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <div className="flex gap-x-12 items-center">
        <ActionButtonLink
          to="/insurances/$guid"
          params={{ guid: row.original.guid }}
          icon={IconAngleRight}
          size="small"
        />
      </div>
    ),
  },
];

function mapCustomerToIcon(customer: Customer) {
  return match(customer.type)
    .returnType<ReactNode>()
    .with('consumer', () => <IconUserCircle className="size-24" />)
    .with('organization', () => <IconBuilding className="size-24" />)
    .exhaustive();
}

export function useInsuranceTable(data?: Insurances) {
  return useReactTable({
    columns,
    data: data?.response ?? [],
    rowCount: data?.response.length,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    pageCount: data?.pagination.totalPages,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });
}
